import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  CHART_NEGATIVE,
  CHART_POSITIVE,
  DataTable,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  StackedBarChart,
  StatTile,
} from "@empite/ui-components";
import { isApiError } from "@empite/api-client";
import type {
  ApplicationEntitlement,
  ApplicationSummary,
  AudienceTier,
  IntegrationEndpoints,
  OAuthClient,
  ProviderModel,
  RoutingProfileDetail,
} from "../admin-client";
import { AUDIENCE_TIERS } from "../admin-client";
import {
  useAnalyticsApplications,
  useApplication,
  useRevokeEntitlement,
  useProviderModels,
  useRoutingProfile,
  useRoutingProfiles,
  useSetApplicationStatus,
  useSetEntitlementTier,
  useSetOAuthClientStatus,
} from "../admin-hooks";
// The alias blocks are the routing-profile page's, imported rather than
// re-derived: this card and that page are two readings of the same rows, and
// two copies would drift into two answers about which alias is live.
import { AudienceControl, audienceLookup } from "./model-policies";
import type { Operator } from "../admin-client";
import { ErrorAlert, RequireOperator, can, errorMessage } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { ApplicationEditForm, OAuthClientForm, RedirectURIEditor } from "./application-form";
import type { ClientFormTarget } from "./application-form";
import { ScopeList, StatusBadge, when } from "./common";
import { MONEY, READ, WITHHELD, WindowNotices, WindowPicker, money } from "./analytics-common";
import { formatRate, windowMeasured } from "./analytics-window";
import type { AnalyticsSearch } from "./analytics-window";
import { outcomeSeries } from "./analytics-charts";
import {
  applicationRow,
  formatMs,
  hasTraffic,
  latencyBasisLabel,
  latencyDaysNote,
  latencyFigures,
  latencyMeasured,
  settlementState,
} from "./analytics-application-usage";

/** The permission the Admin API guards every write on this page with. The
 *  string is the server's; it is repeated here only to decide what to draw. */
const WRITE = "applications:write";

/** The catalogue permissions. Reading an application's models is reading its
 *  routing profile, which is `providers:read` — a different grant from the one
 *  that opens this page, so `support` sees the application and not its
 *  models. */
const PROVIDERS_READ = "providers:read";
const PROVIDERS_WRITE = "providers:write";

/**
 * One application: its OAuth clients and who is entitled to it.
 *
 * The clients are the interesting half. A redirect URI and an allowed scope
 * list are what an application can actually ask a customer for, so they are
 * shown in full rather than summarised — an operator investigating "why did
 * this app get that scope" is reading exactly these two columns.
 */
export function ApplicationDetail({
  applicationId,
  search,
}: {
  applicationId: string;
  search: AnalyticsSearch;
}) {
  return (
    <RequireOperator>
      {(session) => (
        <ApplicationPanels
          applicationId={applicationId}
          search={search}
          operator={session.operator}
        />
      )}
    </RequireOperator>
  );
}

function ApplicationPanels({
  applicationId,
  search,
  operator,
}: {
  applicationId: string;
  search: AnalyticsSearch;
  operator: Operator;
}) {
  const application = useApplication(applicationId);
  // One banner for the page: the three cards below all mutate, and an
  // operator who disabled a client at the bottom still has to see it worked.
  const [notice, setNotice] = useState<string | null>(null);

  if (application.isPending) {
    return <Spinner label="Loading the application" />;
  }
  if (application.error || !application.data) {
    return <ErrorAlert error={application.error} title="Could not load the application" />;
  }

  const {
    application: app,
    oauth_clients: clients,
    entitlements,
    integration,
  } = application.data;

  return (
    <>
      <PageHeader
        title={app.name}
        description={
          <>
            <code>{app.slug}</code> in the {app.suite_name} suite ·{" "}
            <Link to="/applications">back to all applications</Link>
          </>
        }
        actions={<StatusBadge status={app.status} />}
      />
      {notice ? <Alert tone="success" title={notice} /> : null}
      <ApplicationCard app={app} operator={operator} onDone={setNotice} />
      {/* Absent, not empty, without the grant — see UsageCard. The check is
          here rather than inside it so the query is never issued either: the
          Admin API's Guard writes every refusal to the audit trail, and a
          console that fires a read it knows will be refused fills that trail
          with denials nobody chose to attempt.

          Second on the page, directly under what the application IS. The two
          cards below it are the security half — who can ask a customer for
          what, and who has been granted it — and an operator reading those is
          not asking how the product is doing. */}
      {can(operator, READ) ? (
        <UsageCard applicationId={applicationId} slug={app.slug} search={search} />
      ) : null}
      <ClientsCard
        applicationId={applicationId}
        clients={clients}
        operator={operator}
        onDone={setNotice}
      />
      {/* Under the clients card on purpose: this is what to DO with the
          client that card registers, and its values are that card's rows read
          back in the shape a build consumes. */}
      <IntegrationCard app={app} clients={clients} endpoints={integration} />
      <EntitlementsCard
        applicationId={applicationId}
        entitlements={entitlements}
        operator={operator}
        onDone={setNotice}
      />
      {/* Last, because it is the widest and the only card that reads two more
          endpoints. It is also the card an operator arrives for when the
          question is "why can this customer not see the new model", and that
          question is asked after the entitlement above has been checked. */}
      <ModelsCard applicationId={applicationId} operator={operator} onDone={setNotice} />
    </>
  );
}

/**
 * Which models this application may call, and who has them yet.
 *
 * The models an application serves are its routing profile's model policies —
 * there is no second list, and inventing one here would be a second answer to
 * "may this application call Opus" that could disagree with the resolver. So
 * this card reads the profile and renders the SAME alias blocks the
 * routing-profile page renders, from the same module.
 *
 * It shows the APPLICATION-WIDE DEFAULT profile only. An application may also
 * carry one profile per organization (migration 0006), and those are real but
 * they are an override for one customer rather than a statement about the
 * product; mixing them into one list would make "this application serves six
 * models" untrue for everybody except the organization whose profile happened
 * to be included. The link to the full profile page is right there for the
 * cases that need them.
 */
function ModelsCard({
  applicationId,
  operator,
  onDone,
}: {
  applicationId: string;
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const canRead = can(operator, PROVIDERS_READ);
  const canWrite = can(operator, PROVIDERS_WRITE);
  // Not fetched at all without the grant, for the reason UsageCard is not: the
  // Admin API audits every refusal, so a read the console knows will be denied
  // writes a denial nobody chose to attempt.
  const profiles = useRoutingProfiles(canRead);
  const summary = (profiles.data?.routing_profiles ?? []).find(
    (profile) =>
      profile.application_id === applicationId &&
      profile.organization_id === undefined &&
      profile.status === "active",
  );
  const detail = useRoutingProfile(summary?.id ?? "", summary !== undefined);
  // Read for the display names only. The policies carry ids; an operator reads
  // "Claude Sonnet 4.5".
  const catalogue = useProviderModels(canRead);

  if (!canRead) {
    return null;
  }

  return (
    <Card
      title="Models"
      // One way out, and no controls in the card itself.
      //
      // The routing profile IS this application's routing — the same rows, the
      // same policies — so a second screen that could edit them would be two
      // places owning one decision, and the two would disagree the first time
      // somebody used the wrong one. This card reports; the profile page
      // changes. That also keeps the things this card cannot express (several
      // accounts of one provider serving a model, the order they are tried in)
      // from being silently unavailable rather than visibly elsewhere.
      actions={
        summary ? (
          <Link
            className="esm-button esm-button--secondary"
            to="/routing-profiles/$profileId"
            params={{ profileId: summary.id }}
          >
            {canWrite ? "Manage on the routing profile" : "Open the routing profile"}
          </Link>
        ) : null
      }
    >
      <ErrorAlert error={profiles.error} title="The routing profiles could not be read" />
      {profiles.isPending ? <Spinner label="Finding this application's routing profile" /> : null}
      {!profiles.isPending && summary === undefined ? (
        <p className="esm-muted-text">
          This application has no active routing profile, so it can call no model at all: a
          request from it finds no profile and is refused before any model is looked up. Create
          one from the <Link to="/routing-profiles">routing profiles</Link> page.
        </p>
      ) : null}
      {summary !== undefined ? (
        <ApplicationModels detail={detail} catalogue={catalogue.data?.models ?? []} />
      ) : null}
    </Card>
  );
}

/** The alias list for one profile, split out so the card above stays a layout
 *  and the loading states live next to what they are loading. */
/**
 * The models this application may call — a report, not a control panel.
 *
 * It answers one question: which models can this product's clients ask for, and
 * who has each of them yet. Nothing here writes. The routing profile owns these
 * rows, and a second screen that could also edit them would be two places
 * owning one decision.
 *
 * It also deliberately does NOT reuse the routing-profile page's alias block.
 * That component answers "how does this name resolve" — it leads with the
 * alias, ranks the routes, says which is tried first, and shows each one's
 * token ceilings and capability gates. All of that is the fallback chain, and
 * it belongs where the chain is the subject. Here a row is a MODEL, named the
 * way the catalogue names it; the providers behind it are an unordered fact. A
 * model two providers serve is one row, because to this application it is one
 * model.
 */
function ApplicationModels({
  detail,
  catalogue,
}: {
  detail: ReturnType<typeof useRoutingProfile>;
  catalogue: ProviderModel[];
}) {
  if (detail.isPending) {
    return <Spinner label="Loading this application's models" />;
  }
  if (detail.error || !detail.data) {
    return <ErrorAlert error={detail.error} title="Could not load this application's models" />;
  }

  const { model_policies: policies, model_audiences: audiences } = detail.data;
  const audienceFor = audienceLookup(audiences);
  const rows = assignedModels(policies, catalogue);

  if (rows.length === 0) {
    return (
      <p className="esm-muted-text">
        This application can call no model yet. Assign one on its routing profile and its clients
        will be able to ask for it by name.
      </p>
    );
  }

  return (
    <>
      <p className="esm-muted-text">
        Availability is who may actually use each model — the team, external testers, or everyone.
        Assigning a model, removing one, changing who may use it, and choosing the order providers
        are tried in are all done on the routing profile.
      </p>
      <DataTable<AssignedModel>
        rows={rows}
        rowKey={(row) => row.name}
        empty="No model is assigned."
        columns={[
          { key: "model", header: "Model", render: (row) => <div>{row.displayName}</div> },
          {
            key: "providers",
            header: "Served by",
            // Names, unordered. Which one answers first is the profile's
            // business; here it is only the fact that more than one can.
            render: (row) => (
              <div className="esm-badge-row">
                {row.providers.map((provider) => (
                  <Badge key={provider} tone="neutral">
                    {provider}
                  </Badge>
                ))}
              </div>
            ),
          },
          {
            key: "availability",
            header: "Availability",
            // A model switched off for this application is off whatever ring it
            // was released to, so the ring would be a true statement that reads
            // as a false one. Say the more important thing.
            // The same control the routing profile uses, with writing switched
            // off — it renders as a badge then. Reusing it rather than drawing
            // a second badge keeps the words for a ring in one place, so this
            // card and that page cannot come to call `internal` different
            // things.
            render: (row) =>
              row.disabled ? (
                <Badge tone="danger">off for this application</Badge>
              ) : (
                <AudienceControl
                  alias={row.name}
                  label={row.displayName}
                  profileId=""
                  audience={audienceFor(row.name)}
                  canWrite={false}
                  onDone={() => undefined}
                />
              ),
          },
        ]}
      />
    </>
  );
}

/** One model this application may call, however many providers serve it. */
interface AssignedModel {
  /** True when NO route for it is still permitted — the model has been
   *  switched off for this application on its routing profile. */
  disabled: boolean;
  /** The name clients send. Used as the key and as the audience's subject; it
   *  is not shown, because an operator reads the display name. */
  name: string;
  displayName: string;
  providers: string[];
  /** Every catalogue row behind the name, so removing it removes all of them. */
  modelIds: string[];
}

/**
 * Folds the profile's policies into one row per model.
 *
 * The display name comes from the CATALOGUE rather than from the policy, which
 * carries only ids. When the catalogue has not loaded — or a policy points at a
 * row that has since gone — the name a client sends is the honest fallback: it
 * is a true name for the thing, just a plainer one.
 */
function assignedModels(
  policies: RoutingProfileDetail["model_policies"],
  catalogue: ProviderModel[],
): AssignedModel[] {
  const byPair = new Map(catalogue.map((row) => [`${row.provider}\u0000${row.model}`, row]));
  const byName = new Map<string, AssignedModel>();

  for (const policy of policies) {
    const row = byPair.get(`${policy.provider}\u0000${policy.provider_model}`);
    const existing = byName.get(policy.model);
    if (existing) {
      if (!existing.providers.includes(policy.provider)) {
        existing.providers.push(policy.provider);
      }
      if (row && !existing.modelIds.includes(row.id)) {
        existing.modelIds.push(row.id);
      }
      // Off only when EVERY route is: one permitted route is a model that
      // still answers, and reporting it as off would be worse than saying
      // nothing.
      existing.disabled = existing.disabled && !policy.allowed;
      continue;
    }
    byName.set(policy.model, {
      name: policy.model,
      displayName: row?.display_name || policy.model,
      providers: [policy.provider],
      modelIds: row ? [row.id] : [],
      disabled: !policy.allowed,
    });
  }
  // Alphabetical: there is no order between two models an application may call,
  // so the only useful arrangement is the one somebody can find a name in.
  return [...byName.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function ApplicationCard({
  app,
  operator,
  onDone,
}: {
  app: ApplicationSummary;
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const setStatus = useSetApplicationStatus();
  const [editing, setEditing] = useState(false);
  const next = app.status === "active" ? "disabled" : "active";

  return (
    <Card title="Application">
      <ErrorAlert error={setStatus.error} title="That status change failed" />
      <dl className="esm-detail-list">
        <dt>Identifier</dt>
        <dd>
          <code>{app.id}</code>
        </dd>
        <dt>Suite</dt>
        <dd>
          {app.suite_name} (<code>{app.suite_slug}</code>)
        </dd>
        <dt>Status</dt>
        <dd>
          <StatusBadge status={app.status} />
        </dd>
        <dt>Note</dt>
        <dd>
          {/* Three states, kept apart: a note, a note somebody cleared, and a
              note nobody ever wrote. 0031 made the column nullable for exactly
              this, and the edit form's save depends on the difference. */}
          {app.description === null ? (
            <span className="esm-muted-text">never written</span>
          ) : app.description === "" ? (
            <span className="esm-muted-text">cleared</span>
          ) : (
            app.description
          )}
        </dd>
        <dt>Registered</dt>
        <dd>{when(app.created_at)}</dd>
      </dl>
      {/* Keyed on the application and NOT on its values: the boxes are seeded
          from the row when the form mounts, and toggling it shut and open again
          remounts it, so a save is followed by fresh values either way. Keying
          on the values as well would reset an operator's half-typed note the
          moment a background refetch landed. */}
      {editing ? (
        <ApplicationEditForm
          key={app.id}
          app={app}
          onClose={() => setEditing(false)}
          onDone={(message) => {
            setEditing(false);
            onDone(message);
          }}
        />
      ) : null}
      <div className="esm-actions">
        {/* Rendered from the operator's permissions as a courtesy — the
            Admin API checks applications:write on the route itself and
            audits the refusal, so hiding this hides a 403, not a capability. */}
        {can(operator, WRITE) ? (
          // Opening the form is not a write, so it is a plain button. The
          // reason prompt sits on Save, where the request actually is.
          <Button variant="secondary" onClick={() => setEditing((open) => !open)}>
            {editing ? "Close the editor" : "Edit application"}
          </Button>
        ) : null}
        {can(operator, WRITE) ? (
          <ReasonButton
            variant={next === "disabled" ? "danger" : "primary"}
            busy={setStatus.isPending}
            mutation={{
              title: next === "disabled" ? `Disable ${app.name}` : `Enable ${app.name}`,
              description:
                next === "disabled" ? (
                  <p>
                    Disabling stops this application being authorized. Existing customers keep
                    their entitlements; nothing new can be granted against it.
                  </p>
                ) : (
                  <p>Enabling lets this application be authorized again.</p>
                ),
              confirmLabel: next === "disabled" ? "Disable application" : "Enable application",
              run: async (reason) => {
                const result = await setStatus.mutateAsync({
                  applicationID: app.id,
                  status: next,
                  reason,
                });
                onDone(`${app.name} is now ${result.status}.`);
              },
            }}
          >
            {next === "disabled" ? "Disable application" : "Enable application"}
          </ReasonButton>
        ) : (
          <span className="esm-muted-text">
            Your roles do not grant <code>{WRITE}</code>.
          </span>
        )}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- *
 * Usage and revenue.
 *
 * The product's own numbers, on the page an operator is already on when
 * somebody asks whether App B is worth keeping. It sits directly under what
 * the application IS, because the two cards below are the security half — the
 * redirect URIs a client can send a customer to, and who has been entitled to
 * it — and an operator auditing those is not asking how the product is doing.
 *
 * AGGREGATES ONLY, and structurally so. The route behind this card returns no
 * request list, no message count, no prompt and no completion; the finest
 * grain in the document is an application and a day. That is the shape of the
 * document rather than an omission from this screen, and there is no route
 * that would answer otherwise.
 * -------------------------------------------------------------------------- */

/** Counts, with separators. One formatter for the tiles and the chart, so an
 *  axis figure and a hidden-table cell cannot disagree about 12000. */
function count(value: number): string {
  return value.toLocaleString();
}

/** Why a latency tile is blank. NOT zero, and the words say which: zero
 *  milliseconds is a figure a reader takes for an excellent result. */
const NOT_MEASURED =
  "Not measured — no request in this window reached that milestone. This is an absent reading, not a zero.";

/**
 * One application's usage and settled money over a window the operator chooses.
 *
 * ITS OWN QUERY, on the rule the account page's Usage card sets and for the
 * same reasons: this read is behind a grant the rest of the page does not
 * need, it is the only read on the page that takes a window, and it is the
 * only one an operator can make fail by hand-editing a URL. Folded into the
 * application read it would have taken the OAuth clients and the entitlements
 * down with it — the two things somebody investigating a scope grant is here
 * for.
 *
 * NOT RENDERED AT ALL without `analytics:read`; see ApplicationPanels. An
 * empty card headed "Usage and revenue" would say this product earned nothing,
 * which is a claim about the business made out of a fact about the reader.
 *
 * Asked by SLUG, which is what the route takes: `?application=` names a
 * resource there, so a slug nothing matches is a 404 rather than an empty list
 * — a typo must not render as "nobody uses this product".
 */
function UsageCard({
  applicationId,
  slug,
  search,
}: {
  applicationId: string;
  slug: string;
  search: AnalyticsSearch;
}) {
  const navigate = useNavigate();
  const usage = useAnalyticsApplications({
    since: search.since,
    until: search.until,
    days: search.days,
    application: slug,
  });
  const data = usage.data;

  // Looked up by slug rather than taken at index 0 — see applicationRow. The
  // answer is a list of one, and "the first row" and "this application" are
  // only the same thing while the request and the response agree.
  const row = useMemo(() => (data ? applicationRow(data, slug) : undefined), [data, slug]);

  // Four states and not two, on the dashboard's rule, and the order of the two
  // that both arrive as zeros matters. `available_from` is the platform's
  // earliest rollup day, so it separates "nobody was counting yet" from "this
  // product had no traffic" — and it is asked FIRST, because a window before
  // the sweep ever ran satisfies both.
  const measured = data !== undefined && windowMeasured(data.available_from, data.window.until);
  const traffic = row !== undefined && hasTraffic(row);

  // Keyed on the row itself, so a keystroke in the window picker above this —
  // which is another component's state — cannot rebuild a series.
  const outcome = useMemo(
    () => (row ? outcomeSeries(row.series, CHART_POSITIVE, CHART_NEGATIVE) : null),
    [row],
  );
  const latency = useMemo(() => (row ? latencyFigures(row.latency) : []), [row]);

  // The response ALONE, with no prediction from the operator's own permission
  // list, on the rule the account card sets: the two-authority dance is for a
  // whole CARD, which appears or does not and shoves the page around when it
  // changes its mind. A withheld FIELD has a slot to sit in either way, and
  // the slot says why it is empty. The `false` is not a default anybody reads
  // — no tile is drawn before `data` arrives.
  const moneyVisible = data?.money_visible ?? false;
  const currency = data?.currency;
  const settlement = settlementState(row?.revenue);
  // A 403 can still arrive: the permission list this console holds is the one
  // from sign-in, and a role changed underneath an open page is exactly the
  // case where the server's answer is the true one.
  const forbidden = isApiError(usage.error) && usage.error.status === 403;

  return (
    <Card title="Usage and revenue">
      <p className="esm-muted-text">
        What this product has cost and earned, from the worker's nightly rollups. Every figure is
        an aggregate: there is no request, no prompt and no completion in this document, and
        nothing on this card opens one.
      </p>

      {/* The window is in the URL rather than in this card's state, so a
          narrowed span can be pasted into a ticket and opened by the next
          person on the same days. The rest of the page ignores it. */}
      <WindowPicker
        search={search}
        busy={usage.isFetching}
        idPrefix="application-usage"
        apply={(next) =>
          void navigate({ to: "/applications/$applicationId", params: { applicationId }, search: next })
        }
      />

      {usage.error ? (
        <Alert
          tone={forbidden ? "info" : "danger"}
          title={forbidden ? "Not your permission" : "The usage figures could not be loaded"}
        >
          {forbidden
            ? `Reading an application's usage needs ${READ}, and your roles no longer grant it. Everything else on this page is unaffected.`
            : errorMessage(usage.error)}
        </Alert>
      ) : null}

      {/* Drawn from the answer's own window, never from the ask: a card that
          was clamped says so itself rather than borrowing a claim from the
          URL that asked. */}
      {data ? (
        <WindowNotices
          window={data.window}
          currency={data.currency}
          currencies={data.currencies}
          search={search}
        />
      ) : null}

      {usage.isPending && !usage.error ? <Spinner label="Loading the usage figures" /> : null}

      {data && !measured ? (
        <EmptyState title="Nothing has been measured over these days">
          <p>
            {data.available_from === null
              ? "No daily rollup has been written yet, so there is nothing to report about any application — not even a zero. The worker writes them nightly."
              : `The rollups start on ${data.available_from}, which is after this window ends. Ask about a later window to see figures.`}
          </p>
          <p>
            This is not the same as this application having been idle. Zeros are not shown here on
            purpose: they would say the product earned nothing on days nobody was measuring.
          </p>
        </EmptyState>
      ) : null}

      {data && measured && !row ? (
        <Alert tone="warning" title="The answer carried no row for this application">
          The read succeeded and named no figures for <code>{slug}</code>. Every application in the
          catalogue gets a row — a quiet one gets zeros — so this is a disagreement between the
          page and the answer rather than a measurement.
        </Alert>
      ) : null}

      {data && measured && row ? (
        <>
          <div className="esm-stat-grid">
            <StatTile
              label="Requests"
              value={count(row.requests)}
              hint={`${count(row.failed_requests)} of them failed.`}
            />
            <StatTile
              label="Failure rate"
              value={formatRate(row.failure_rate)}
              unavailable="The rate could not be computed from this answer."
              hint="Failed requests over requests, inside this window."
            />
            <StatTile
              label="Fallback rate"
              value={formatRate(row.fallback_rate)}
              unavailable="The rate could not be computed from this answer."
              hint={`${count(row.fallback_requests)} of ${count(row.requests)} requests were answered after a fallback. A rising share is a primary provider in trouble rather than a fact about this product.`}
            />
            <StatTile
              label="Streamed"
              value={formatRate(row.stream_share)}
              unavailable="The share could not be computed from this answer."
              hint={`${count(row.stream_requests)} of ${count(row.requests)} requests streamed. The first-token figures below are taken over these alone, so a low share makes them a statement about very few requests.`}
            />
            <StatTile
              label="Total tokens"
              value={count(row.total_tokens)}
              hint={`${count(row.input_tokens)} in, ${count(row.output_tokens)} out, ${count(row.cached_input_tokens)} cached, ${count(row.cache_write_tokens)} cache writes, ${count(row.reasoning_tokens)} reasoning.`}
            />
            <StatTile
              // "up to", because the figure is an upper bound and the wire
              // says so in its name: the rollup stores a per-day distinct
              // count and never the ids, so a person who used the app on ten
              // days contributes ten.
              label="People"
              value={`up to ${count(row.distinct_users_upper_bound)}`}
              hint="An upper bound over the window. Who they are is the leaderboard on /analytics, which ranks accounts rather than products."
            />
            <StatTile
              label="Devices"
              value={`up to ${count(row.distinct_devices_upper_bound)}`}
              hint="An upper bound over the window, counted the same way. A request from a service caller has no device and is in the request count but not in this one."
            />
            <StatTile
              label="Provider cost"
              value={money(row.cost_micros, moneyVisible, currency)}
              unavailable={WITHHELD}
              hint="What this application's traffic cost upstream, as the sweep priced it."
            />
            <StatTile
              label="Revenue (billed)"
              value={money(row.sell_micros, moneyVisible, currency)}
              unavailable={WITHHELD}
              hint="What the sweep charged for that traffic. What actually settled is below, and the two are not expected to agree."
            />
            <StatTile
              label="Margin (billed)"
              value={money(row.margin_micros, moneyVisible, currency)}
              unavailable={WITHHELD}
              hint="Revenue minus cost. Negative is a real reading and the one this card exists to surface — a written-off request costs money that was never charged for."
            />
          </div>

          {row.status !== "active" ? (
            <p className="esm-muted-text">
              This application is <StatusBadge status={row.status} />, so a collapsing tail in the
              figures above is a measurement of the disabling rather than of the product.
            </p>
          ) : null}

          {!traffic ? (
            <p className="esm-muted-text">
              <strong>No traffic in this window.</strong> Every figure above is a measured zero:
              the rollups cover {data.window.since} to {data.window.until} and this application
              made no request in them. That is an answer, not a failed read — the row exists
              because the catalogue is read rather than the rollup, which is what stops a product
              nobody used from disappearing off this surface entirely.
            </p>
          ) : null}

          <h3>Requests and failures per day</h3>
          <p className="esm-muted-text">
            Every day of the window, including the quiet ones — a day with no traffic is drawn as
            the zero it was measured to be, never as a break. Successes sit on the baseline; the
            failures cap each bar.
          </p>
          {traffic && outcome ? (
            <StackedBarChart
              name="Requests by outcome"
              labels={outcome.labels}
              series={outcome.series}
              format={count}
              pointHeader="Day"
            />
          ) : (
            <p className="esm-muted-text">
              Nothing to draw: every day of this window is a measured zero, and a chart of
              zero-height bars is indistinguishable from one that failed to load.
            </p>
          )}

          <h3>How fast it answered</h3>
          <p className="esm-muted-text">
            {latencyBasisLabel(row.latency.basis)} A figure with no measurement behind it is left
            blank and said in words — zero milliseconds would read as an excellent result rather
            than as an absent one.
          </p>
          {!latencyMeasured(row.latency) ? (
            <p className="esm-muted-text">
              No request in this window reached either milestone, so all four are blank. A window
              whose requests all failed before the first token has no population to take a
              first-token percentile of, and that is a real answer.
            </p>
          ) : null}
          <div className="esm-stat-grid">
            {latency.map((figure) => (
              <StatTile
                key={figure.key}
                label={figure.label}
                value={formatMs(figure.ms)}
                unavailable={NOT_MEASURED}
                hint={latencyDaysNote(figure.days, data.window.days)}
              />
            ))}
          </div>

          <h3>What it settled</h3>
          {settlement === "withheld" ? (
            <p className="esm-muted-text">
              The settled money is absent rather than zero: it needs <code>{MONEY}</code>, which
              this account does not hold. The whole block is withheld rather than drawn empty —
              every figure in it is money, so there would be nothing left in it to show.
            </p>
          ) : (
            <>
              <p className="esm-muted-text">
                Read from the revenue rollup, so these are figures that actually MOVED — unlike
                the billed pair in the tiles above, which is what the sweep priced the traffic at.
                The gap between the two is itself worth reading.
              </p>
              {settlement === "quiet" ? (
                <p className="esm-muted-text">
                  Nothing settled against this application in this window. These are measured
                  zeros, on the same footing as the usage figures above.
                </p>
              ) : null}
              <div className="esm-stat-grid">
                <StatTile
                  label="Settled revenue"
                  value={money(row.revenue?.settled_sell_micros, moneyVisible, currency)}
                  unavailable={WITHHELD}
                  hint="Inference charged against this application's wallets."
                />
                <StatTile
                  label="Settled cost"
                  value={money(row.revenue?.settled_cost_micros, moneyVisible, currency)}
                  unavailable={WITHHELD}
                  hint="What the platform paid upstream for the same days."
                />
                <StatTile
                  label="Settled margin"
                  value={money(row.revenue?.settled_margin_micros, moneyVisible, currency)}
                  unavailable={WITHHELD}
                  hint="Revenue minus cost, computed from the two figures beside it rather than stored — so there is no third number free to disagree with them."
                />
                <StatTile
                  label="Top-ups"
                  value={money(row.revenue?.topup_micros, moneyVisible, currency)}
                  unavailable={WITHHELD}
                  hint="Money paid into wallets. Not revenue: it is spent later, or never."
                />
                <StatTile
                  label="Subscription charges"
                  value={money(row.revenue?.subscription_charge_micros, moneyVisible, currency)}
                  unavailable={WITHHELD}
                  hint="What renewals collected against this application in the window."
                />
                <StatTile
                  label="Refunds"
                  value={money(row.revenue?.refund_micros, moneyVisible, currency)}
                  unavailable={WITHHELD}
                  hint="A positive magnitude, as the rollup stores it. It is already subtracted in the net figure beside this one."
                />
                <StatTile
                  label="Net payments"
                  value={money(row.revenue?.net_payments_micros, moneyVisible, currency)}
                  unavailable={WITHHELD}
                  hint="Top-ups plus subscription charges, less refunds."
                />
                <StatTile
                  label="Paying accounts"
                  value={
                    row.revenue === undefined
                      ? null
                      : `up to ${count(row.revenue.paying_users_upper_bound)}`
                  }
                  unavailable={WITHHELD}
                  hint="An upper bound: the rollup stores a per-day count, so a customer who paid in three different weeks is counted three times."
                />
              </div>
            </>
          )}
        </>
      ) : null}
    </Card>
  );
}

function ClientsCard({
  applicationId,
  clients,
  operator,
  onDone,
}: {
  applicationId: string;
  clients: OAuthClient[];
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const setStatus = useSetOAuthClientStatus();
  const writable = can(operator, WRITE);
  const [target, setTarget] = useState<ClientFormTarget | null>(null);
  // The callback editor is its own panel and not a field in the client form,
  // because the API keeps it as its own route: a `redirect_uris` key on the
  // client PATCH is refused by name. Held as an ID rather than as the row, so
  // the panel always works from what the page last READ — a PUT replaces
  // whatever is registered now, and the "this will unregister X" warning has
  // to be about that list rather than about a snapshot taken minutes ago.
  const [callbacksFor, setCallbacksFor] = useState<string | null>(null);
  const callbackClient = clients.find((row) => row.id === callbacksFor) ?? null;

  return (
    <Card
      title="OAuth clients"
      actions={
        writable ? (
          <Button
            variant="primary"
            onClick={() => {
              setCallbacksFor(null);
              setTarget({ mode: "create" });
            }}
          >
            Register a client
          </Button>
        ) : null
      }
    >
      <ErrorAlert error={setStatus.error} title="That client could not be changed" />
      {/* Keyed by what the form is for, so clicking Edit on a second client
          rebuilds the state rather than leaving the first one's values in the
          boxes under a new heading. */}
      {writable && target ? (
        <OAuthClientForm
          key={target.mode === "create" ? "create" : target.client.id}
          applicationID={applicationId}
          target={target}
          onClose={() => setTarget(null)}
          onDone={(message) => {
            setTarget(null);
            onDone(message);
          }}
        />
      ) : null}
      {/* Keyed by client for the same reason as the form above: the rows are
          seeded from the row when the panel mounts, so opening a second
          client's callbacks must be a fresh list rather than the first one's
          under a new heading. */}
      {writable && callbackClient ? (
        <RedirectURIEditor
          key={callbackClient.id}
          applicationID={applicationId}
          client={callbackClient}
          onClose={() => setCallbacksFor(null)}
          onDone={(message) => {
            setCallbacksFor(null);
            onDone(message);
          }}
        />
      ) : null}
      <DataTable<OAuthClient>
        rows={clients}
        rowKey={(row) => row.id}
        empty="No OAuth client is registered against this application."
        columns={[
          {
            key: "name",
            header: "Client",
            render: (row) => (
              <>
                <div>{row.name}</div>
                <code className="esm-muted-text">{row.client_id}</code>
              </>
            ),
          },
          {
            key: "redirects",
            header: "Redirect URIs",
            render: (row) => (
              <ul className="esm-scope-list">
                {row.redirect_uris.map((uri) => (
                  <li key={uri}>
                    {/* Rendered as text, never as a link: this is a list an
                        operator is auditing, and a console that offers to
                        navigate to an attacker-registered callback is a
                        console that can be used to reach one. */}
                    <code>{uri}</code>
                  </li>
                ))}
              </ul>
            ),
          },
          {
            key: "scopes",
            header: "Allowed scopes",
            render: (row) => <ScopeList scopes={row.allowed_scopes} />,
          },
          {
            key: "pkce",
            header: "PKCE",
            render: (row) => (
              // A public client without PKCE is the finding, so it is called
              // out rather than left as an empty cell.
              <Badge tone={row.require_pkce ? "success" : "warning"}>
                {row.require_pkce ? "required" : "not required"}
              </Badge>
            ),
          },
          {
            key: "auth",
            header: "Auth method",
            render: (row) => <code>{row.token_endpoint_auth_method}</code>,
          },
          { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
          {
            key: "toggle",
            header: "",
            align: "end",
            render: (row) => {
              if (!writable) {
                return null;
              }
              const next = row.status === "active" ? "disabled" : "active";
              return (
                <div className="esm-actions">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setCallbacksFor(null);
                    setTarget({ mode: "edit", client: row });
                  }}
                >
                  Edit
                </Button>
                {/* Opening an editor is not a write, so it is a plain button.
                    The reason prompt sits on Save, where the request is. */}
                <Button
                  variant="ghost"
                  onClick={() => {
                    setTarget(null);
                    setCallbacksFor(row.id);
                  }}
                >
                  Callbacks
                </Button>
                <ReasonButton
                  variant={next === "disabled" ? "danger" : "secondary"}
                  busy={setStatus.isPending && setStatus.variables?.clientID === row.id}
                  mutation={{
                    title: next === "disabled" ? `Disable ${row.name}` : `Enable ${row.name}`,
                    description: (
                      <p>
                        Client <code>{row.client_id}</code>.{" "}
                        {next === "disabled"
                          ? "Disabling refuses its authorize and token requests from now on; anything already signed in keeps its session until it expires or is revoked."
                          : "Enabling lets it request authorization again."}
                      </p>
                    ),
                    confirmLabel: next === "disabled" ? "Disable client" : "Enable client",
                    run: async (reason) => {
                      const result = await setStatus.mutateAsync({
                        clientID: row.id,
                        status: next,
                        reason,
                        applicationID: applicationId,
                      });
                      onDone(`${row.name} is now ${result.status}.`);
                    },
                  }}
                >
                  {next === "disabled" ? "Disable" : "Enable"}
                </ReasonButton>
                </div>
              );
            },
          },
        ]}
      />
    </Card>
  );
}

/**
 * How to point a desktop client at THIS application.
 *
 * It exists because the four values a client has to be built with are spread
 * across three screens and one migration note, and getting any of them wrong
 * fails in a way that does not name the mistake: an unregistered callback and
 * an unknown client_id come back as the same "not registered" sentence, on
 * purpose, so that an unauthenticated caller cannot probe for either.
 *
 * The block is rendered PER CLIENT rather than once per application. A client
 * is the thing a build names, an application may carry several — a shipped
 * build and an internal one, or two products of one suite — and a guide that
 * showed the application's values with a client picker beside it would be a
 * guide somebody copies before choosing.
 *
 * `tenant` is the value nothing else on this page gives you and the one whose
 * absence is silent: `client_id` is unique per tenant (0057), an omitted
 * `tenant` means the PLATFORM's own, and a client built without it does not
 * fail — it authenticates against somebody else's namespace and is told the
 * client is not registered. So it leads the block.
 *
 * Nothing here is a secret. A public client has no secret to leak — PKCE is
 * required and `token_endpoint_auth_method` is `none` — and the callbacks are
 * already listed in the card above. What would be a disclosure is the tenant
 * handle of a company this operator cannot see, and the application document
 * carrying it is already scoped to what they can.
 */
function IntegrationCard({
  app,
  clients,
  endpoints,
}: {
  app: ApplicationSummary;
  clients: OAuthClient[];
  endpoints: IntegrationEndpoints;
}) {
  // Disabled clients are shown too, marked. Leaving one out would answer
  // "which of my builds is configured how" with silence for the build that is
  // currently switched off, which is exactly the one somebody is debugging.
  const usable = [...clients].sort((a, b) => a.client_id.localeCompare(b.client_id));

  return (
    <Card title="Connect a desktop client">
      <p className="esm-muted-text">
        Everything a build needs to reach <strong>{app.name}</strong>, with this
        deployment&rsquo;s own origins filled in. The one value nothing else on this page gives
        you is <code>tenant</code>: a <code>client_id</code> is unique per company, not
        platform-wide, and a request that omits the handle is read in the{" "}
        <em>platform&rsquo;s</em> namespace rather than in yours.
      </p>

      {usable.length === 0 ? (
        <p className="esm-muted-text">
          This application has no OAuth client yet, and a desktop build has nothing to name
          without one. Register a client above, give it the loopback callbacks it will listen
          on, and the settings for it appear here.
        </p>
      ) : (
        usable.map((client) => (
          <div className="esm-config-client" key={client.id}>
            <h4>
              {client.name} <code className="esm-muted-text">{client.client_id}</code>{" "}
              {client.status === "active" ? null : <StatusBadge status={client.status} />}
            </h4>
            <ConfigBlock text={clientConfig(app, client, endpoints)} label={client.name} />
            {client.redirect_uris.length === 0 ? (
              <Alert tone="warning" title="This client has no registered callback">
                <p>
                  The authorization step is refused before it starts. Add the loopback addresses
                  the build listens on, using <strong>Callbacks</strong> above.
                </p>
              </Alert>
            ) : (
              <ClientRequests app={app} client={client} endpoints={endpoints} />
            )}
          </div>
        ))
      )}

      <p className="esm-muted-text">
        A client that omits <code>tenant</code> keeps working against the platform&rsquo;s own
        applications, which is why the parameter is optional — it is not optional for yours.
        The full contract, including the device proof, the refresh cycle and the error
        shapes, is in <Link to="/guide">the desktop client guide</Link>.
      </p>
    </Card>
  );
}

/**
 * The three calls, with this client's values already in them.
 *
 * Written as raw requests rather than as prose about requests: what somebody
 * needs at this moment is the URL, the method and the field names, and a
 * paragraph describing a JSON body is a paragraph they have to turn back into
 * one. The placeholders left in angle brackets are the values only the client
 * can produce — the PKCE pair, the installation key, the code — and everything
 * a person could get wrong by copying is filled in.
 *
 * The first REGISTERED callback is the worked example. A client usually has
 * several and tries them in order, so the first is the one it will actually
 * bind, and showing a list here would leave somebody to guess which one the
 * two steps have to agree on.
 */
function ClientRequests({
  app,
  client,
  endpoints,
}: {
  app: ApplicationSummary;
  client: OAuthClient;
  endpoints: IntegrationEndpoints;
}) {
  // Guarded by the caller: this renders only when a callback is registered,
  // because every request below has to name one and an example built on a
  // placeholder would be an example that cannot be run.
  const callback = client.redirect_uris[0] ?? "";

  return (
    <>
      <p className="esm-muted-text">
        <strong>1 — open the system browser here.</strong> The PORTAL, not the API:{" "}
        <code>/v1/auth/oauth/authorize</code> answers problem+json to a browser with no
        session, because there is no login page behind it. The portal signs the person in and
        forwards the request.
      </p>
      <ConfigBlock
        text={authorizeRequest(app, client, endpoints, callback)}
        label={`the authorize URL for ${client.name}`}
      />

      <p className="esm-muted-text">
        <strong>2 — exchange the code your listener caught.</strong> Send{" "}
        <code>tenant</code> again: it is bound onto the code at issue and compared at
        redemption, ahead of the <code>client_id</code> check. The{" "}
        <code>redirect_uri</code> must be the byte-identical one from step 1 — the platform
        stored it with the code. Unknown top-level fields are a 400, so send exactly these.
      </p>
      <ConfigBlock
        text={tokenRequest(app, client, endpoints, callback)}
        label={`the token request for ${client.name}`}
      />
      <p className="esm-muted-text">
        200 answers <code>access_token</code>, <code>token_type</code>,{" "}
        <code>expires_in</code>, <code>refresh_token</code> and <code>scope</code>. Keep the
        refresh token and the installation private key in the OS keystore; the access token
        belongs in memory only.
      </p>

      <p className="esm-muted-text">
        <strong>3 — call the application.</strong> Both halves are mandatory: the bearer token
        says who, and the proof — an EdDSA JWS over this exact request, signed by the
        installation key — says which machine.
      </p>
      <ConfigBlock
        text={callRequest(app, endpoints)}
        label={`an application call for ${app.slug}`}
      />

      <p className="esm-muted-text">
        <strong>Renewing</strong> is the same endpoint with the other grant, and it carries{" "}
        <code>tenant</code> too.
      </p>
      <ConfigBlock
        text={refreshRequest(app, client, endpoints)}
        label={`the refresh request for ${client.name}`}
      />
    </>
  );
}

/**
 * Step 1, as a URL.
 *
 * `redirect_uri` is percent-encoded because it is a URL inside a query string
 * and its own `:` and `/` would otherwise end the parameter early. Everything
 * else is a plain token.
 */
function authorizeRequest(
  app: ApplicationSummary,
  client: OAuthClient,
  endpoints: IntegrationEndpoints,
  callback: string,
): string {
  return [
    `${endpoints.customer_portal_url}/authorize`,
    `  ?tenant=${app.tenant_slug}`,
    `  &client_id=${client.client_id}`,
    `  &redirect_uri=${encodeURIComponent(callback)}`,
    `  &response_type=code`,
    `  &state=<random; compare it when the callback fires>`,
    `  &code_challenge=<base64url SHA-256 of the verifier>`,
    `  &code_challenge_method=S256`,
    `  &installation_id=<this machine, stable forever>`,
    `  &installation_public_key=<base64url, 32 raw Ed25519 bytes>`,
  ].join("\n");
}

/** Step 2. JSON rather than form-encoded: both are accepted, but only the JSON
 *  branch can carry `device_details`. */
function tokenRequest(
  app: ApplicationSummary,
  client: OAuthClient,
  endpoints: IntegrationEndpoints,
  callback: string,
): string {
  return [
    `POST ${endpoints.platform_base_url}/v1/auth/oauth/token`,
    `Content-Type: application/json`,
    ``,
    `{`,
    `  "grant_type": "authorization_code",`,
    `  "tenant": "${app.tenant_slug}",`,
    `  "client_id": "${client.client_id}",`,
    `  "code": "<from the callback query string>",`,
    `  "code_verifier": "<the verifier you hashed in step 1>",`,
    `  "redirect_uri": "${callback}",`,
    `  "installation_id": "<the same one you sent in step 1>",`,
    `  "installation_public_key": "<the same key you sent in step 1>",`,
    `  "device_platform": "windows",`,
    `  "device_name": "Jane's laptop"`,
    `}`,
  ].join("\n");
}

/** Step 3. The slug in the path is what the token's audience is held against. */
function callRequest(app: ApplicationSummary, endpoints: IntegrationEndpoints): string {
  return [
    `GET ${endpoints.platform_base_url}/v1/app/${app.slug}/me`,
    `Authorization: Bearer <access_token>`,
    `X-Device-Proof: <EdDSA JWS over this method, URL and body>`,
  ].join("\n");
}

/** The renewal. Same endpoint, same tenant, no installation fields — the
 *  installation is already registered and its key is what the proof uses. */
function refreshRequest(
  app: ApplicationSummary,
  client: OAuthClient,
  endpoints: IntegrationEndpoints,
): string {
  return [
    `POST ${endpoints.platform_base_url}/v1/auth/oauth/token`,
    `Content-Type: application/json`,
    ``,
    `{`,
    `  "grant_type": "refresh_token",`,
    `  "tenant": "${app.tenant_slug}",`,
    `  "client_id": "${client.client_id}",`,
    `  "refresh_token": "<the one you stored>"`,
    `}`,
  ].join("\n");
}

/**
 * The settings for one client, as a file.
 *
 * Written as `KEY=value` lines because that is the shape every one of these
 * builds already reads — the reference client loads a `.env` beside the
 * executable — and because a block that pastes whole is a block nobody
 * transcribes a character of.
 *
 * The callbacks go on ONE line, comma-separated, in the order the server holds
 * them: the client tries them in order and uses the first port it can bind, so
 * a reordering here would be a different first choice there.
 */
function clientConfig(
  app: ApplicationSummary,
  client: OAuthClient,
  endpoints: IntegrationEndpoints,
): string {
  return [
    `PLATFORM_API_URL=${endpoints.platform_base_url}`,
    `CUSTOMER_PORTAL_URL=${endpoints.customer_portal_url}`,
    `OAUTH_TENANT=${app.tenant_slug}`,
    `OAUTH_CLIENT_ID=${client.client_id}`,
    `APPLICATION_SLUG=${app.slug}`,
    `OAUTH_REDIRECT_URI=${client.redirect_uris.join(",")}`,
  ].join("\n");
}

/** A copyable block. The button copies the text on screen and nothing else —
 *  no trailing newline an editor would show as an extra line. */
function ConfigBlock({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="esm-config-block">
      <pre>{text}</pre>
      <button
        type="button"
        className="esm-copy__button esm-config-block__copy"
        aria-label={`Copy the settings for ${label}`}
        onClick={() => {
          void navigator.clipboard?.writeText(text);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
      <span className="esm-visually-hidden" role="status">
        {copied ? `settings for ${label} copied` : ""}
      </span>
    </div>
  );
}

function EntitlementsCard({
  applicationId,
  entitlements,
  operator,
  onDone,
}: {
  applicationId: string;
  entitlements: ApplicationEntitlement[];
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const revoke = useRevokeEntitlement();
  const setTier = useSetEntitlementTier();
  const writable = can(operator, WRITE);

  return (
    <Card title="Entitlements">
      <ErrorAlert error={revoke.error} title="That entitlement could not be revoked" />
      <ErrorAlert error={setTier.error} title="That release ring could not be changed" />
      {/* Revoked entitlements stay in the table, marked. Same rule as the
          customer portal's security screen: a list that drops what it
          revoked gives the operator no way to confirm it happened, and no way
          to answer "was it already revoked when I got here?". */}
      <DataTable<ApplicationEntitlement>
        rows={entitlements}
        rowKey={(row) => row.id}
        empty="Nobody is entitled to this application yet."
        columns={[
          {
            key: "subject",
            header: "Granted to",
            render: (row) => (
              <>
                <div>{row.subject_label}</div>
                <span className="esm-muted-text">
                  {row.subject_type} · <code>{row.subject_id}</code>
                </span>
              </>
            ),
          },
          { key: "scopes", header: "Scopes", render: (row) => <ScopeList scopes={row.scopes} /> },
          { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
          {
            key: "tier",
            header: "Ring",
            // Beside the status on purpose: the ring is only read while the
            // entitlement is active, so the two belong in the same glance.
            render: (row) => (
              <EntitlementTier
                entitlement={row}
                applicationId={applicationId}
                writable={writable}
                onDone={onDone}
              />
            ),
          },
          { key: "granted", header: "Granted", render: (row) => when(row.granted_at) },
          { key: "expires", header: "Expires", render: (row) => when(row.expires_at) },
          {
            key: "revoke",
            header: "",
            align: "end",
            render: (row) =>
              writable && row.status === "active" ? (
                <ReasonButton
                  variant="danger"
                  busy={revoke.isPending && revoke.variables?.entitlementID === row.id}
                  mutation={{
                    title: `Revoke ${row.subject_label}'s entitlement`,
                    description: (
                      <p>
                        This ends the grant of{" "}
                        {row.scopes.length === 0 ? "no scopes" : row.scopes.join(", ")} to{" "}
                        {row.subject_label}. It does not delete anything they have already used.
                      </p>
                    ),
                    confirmLabel: "Revoke entitlement",
                    run: async (reason) => {
                      const result = await revoke.mutateAsync({
                        entitlementID: row.id,
                        reason,
                        applicationID: applicationId,
                      });
                      onDone(`${row.subject_label}'s entitlement is now ${result.status}.`);
                    },
                  }}
                >
                  Revoke
                </ReasonButton>
              ) : null,
          },
        ]}
      />
    </Card>
  );
}

/**
 * Which release ring one subject is in for this application, with the control
 * that moves them.
 *
 * The wire values are `internal | tester | live`; the words here are what a
 * person reads. "Empite only" says what `internal` means where the word itself
 * could be read as anything from a network boundary to a build flag — the same
 * vocabulary the alias control uses, so an operator matching a person to a
 * model is comparing like with like.
 *
 * A ring is only shown as changeable on an ACTIVE entitlement. The server
 * refuses the rest with a 409, because the ring is read only while the subject
 * may use the application at all — so a control on a revoked row would be an
 * offer to make a decision that does nothing.
 */
const TIER_LABELS: Record<AudienceTier, string> = {
  internal: "Empite only",
  tester: "External tester",
  live: "Everyone",
};

function EntitlementTier({
  entitlement,
  applicationId,
  writable,
  onDone,
}: {
  entitlement: ApplicationEntitlement;
  applicationId: string;
  writable: boolean;
  onDone: (message: string) => void;
}) {
  const setTier = useSetEntitlementTier();
  const [choice, setChoice] = useState<AudienceTier>(entitlement.tier);
  const dirty = choice !== entitlement.tier;

  if (!writable || entitlement.status !== "active") {
    return (
      <Badge tone={entitlement.tier === "live" ? "success" : "warning"}>
        {TIER_LABELS[entitlement.tier]}
      </Badge>
    );
  }

  return (
    <div className="esm-actions">
      <Select
        aria-label={`Which ring ${entitlement.subject_label} is in`}
        value={choice}
        onChange={(event) => setChoice(event.target.value as AudienceTier)}
      >
        {AUDIENCE_TIERS.map((tier) => (
          <option key={tier} value={tier}>
            {TIER_LABELS[tier]}
          </option>
        ))}
      </Select>
      {dirty ? (
        <ReasonButton
          variant="primary"
          busy={setTier.isPending && setTier.variables?.entitlementID === entitlement.id}
          mutation={{
            title: `Move ${entitlement.subject_label} to ${TIER_LABELS[choice].toLowerCase()}`,
            tone: "primary",
            confirmLabel: "Change ring",
            description: (
              <>
                <p>
                  {entitlement.subject_label} moves from{" "}
                  <strong>{TIER_LABELS[entitlement.tier].toLowerCase()}</strong> to{" "}
                  <strong>{TIER_LABELS[choice].toLowerCase()}</strong> for this application.
                </p>
                <p>
                  {choice === "live"
                    ? "They see only the models this application has released to everybody."
                    : choice === "tester"
                      ? "They can see and run models held back to external testers, as well as everything released to everybody."
                      : "They can see and run every model this application permits, including ones held back to the team."}
                </p>
              </>
            ),
            run: async (reason) => {
              await setTier.mutateAsync({
                entitlementID: entitlement.id,
                applicationID: applicationId,
                tier: choice,
                reason,
              });
              onDone(
                `${entitlement.subject_label} is now ${TIER_LABELS[choice].toLowerCase()} for this application.`,
              );
            },
          }}
        >
          Save
        </ReasonButton>
      ) : null}
    </div>
  );
}


